'use client'

import { HomeContainer } from '@/components/common/home/home-container'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { ERankingType } from '@/types/auth'
import { useTranslations } from 'next-intl'
import { TableTiers } from './table-tiers'
import { TierItem } from './tier-item'

export const MembershipTiers = () => {
  const t = useTranslations()
  const isMobile = useIsMobileClient()

  const tiers = [
    {
      type: ERankingType.NEW,
      border: 'from-[#203E8F] via-[#809AE2] to-[#203C8B]',
      background: 'bg-[#F9F9FF]',
      benefits: [
        t.rich('landing_coolclub_page.membership.tiers.new.benefit_1', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.new.benefit_2', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.new.benefit_3', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.new.benefit_4', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.new.benefit_5', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.new.benefit_6', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
      ],
    },
    {
      type: ERankingType.SILVER,
      border: 'from-[#737373] via-[#CCCCCC] to-[#737373]',
      background: 'bg-[#F9F9F9]',
      benefits: [
        t.rich('landing_coolclub_page.membership.tiers.silver.benefit_1', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.silver.benefit_2', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.silver.benefit_3', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.silver.benefit_4', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.silver.benefit_5', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.silver.benefit_6', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
      ],
    },
    {
      type: ERankingType.GOLD,
      border: 'from-[#C78E3C] via-[#F1D2A4] to-[#C08B3C]',
      background: 'bg-[#FFF9F1]',
      benefits: [
        t.rich('landing_coolclub_page.membership.tiers.gold.benefit_1', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.gold.benefit_2', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.gold.benefit_3', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.gold.benefit_4', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.gold.benefit_5', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.gold.benefit_6', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
      ],
    },
    {
      type: ERankingType.PLATINUM,
      border: 'from-[#090909] via-[#808080] to-[#151515]',
      background: 'bg-[#E4E4E4]',
      benefits: [
        t.rich('landing_coolclub_page.membership.tiers.platinum.benefit_1', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.platinum.benefit_2', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.platinum.benefit_3', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.platinum.benefit_4', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.platinum.benefit_5', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t.rich('landing_coolclub_page.membership.tiers.platinum.benefit_6', {
          strong: (chunks) => <strong>{chunks}</strong>,
        }),
        t('landing_coolclub_page.membership.tiers.platinum.benefit_7'),
      ],
    },
  ]

  return (
    <>
      <HomeContainer>
        <h2 className='mb-3 text-center font-criteria text-3xl font-semibold leading-9 text-neutral-900'>
          {t('landing_coolclub_page.membership.title')}
        </h2>
        <p className='mb-13 text-center font-sans text-base leading-4.5 text-neutral-900'>
          {t('landing_coolclub_page.membership.description')}
        </p>

        {isMobile ? (
          <Carousel
            opts={{
              align: 'start',
              duration: 50,
              slidesToScroll: 1,
              skipSnaps: true,
            }}
            className='w-full'
          >
            <CarouselContent>
              {tiers.map((tier) => (
                <CarouselItem
                  className='basis-4/5 will-change-transform'
                  key={tier.type}
                >
                  <TierItem
                    type={tier.type}
                    benefits={tier.benefits}
                    background={tier.background}
                    border={tier.border}
                  />
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselNavigation />
          </Carousel>
        ) : (
          <TableTiers />
        )}
      </HomeContainer>
    </>
  )
}

function CarouselNavigation() {
  return (
    <>
      <CarouselPrevious
        className='absolute -left-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-left-10'
        variant='black'
        hideIfStart
      />
      <CarouselNext
        className='absolute -right-4 top-1/2 h-10 w-10 -translate-y-1/2 lg:-right-10'
        variant='black'
        hideIfEnd
      />
    </>
  )
}
